import { Component, inject, OnInit, OnDestroy, computed, ChangeDetectionStrategy } from '@angular/core';
import { RouterLinkActive, RouterLink } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { Observable, filter, takeUntil, tap, Subject } from 'rxjs';
import { Store } from '@ngxs/store';
import { TranslatePipe } from '@ngx-translate/core';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { environment } from 'src/environments/environment';
import { MatTooltip } from '@angular/material/tooltip';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { homeMenuTiles } from '../../configs/homeMenu.config';
import { INavigationItem } from '../../interfaces/INavigationItem';
import { IUser } from '../../interfaces/IUser';
import { USER_ROLES } from '../../enums/UserRoles.enum';
import { UserState } from '../../states/user.state';
import { NavigationOperations } from '../../operations/navigation.operations';
import { UserComponent } from '../user/user.component';
import { HeaderOperationsService } from '../header/header.operations';
import { AppNewsComponent } from '../app-news/app-news.component';
import { SidebarService, SidebarTab } from './../../services/sidebar.service';
import { NotificationsComponent } from '../notifications/notifications.component';

@Component({
  selector: 'app-mobile-nav',
  templateUrl: './mobile-nav.component.html',
  styleUrls: ['./mobile-nav.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterLinkActive,
    RouterLink,
    AsyncPipe,
    TranslatePipe,
    UserComponent,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatTooltip,
    MatTabGroup,
    MatTab,
    NotificationsComponent,
    AppNewsComponent,
  ],
})
export class MobileNavComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private headerOperations = inject(HeaderOperationsService);
  protected sidebarService = inject(SidebarService);

  protected userRole$: Observable<USER_ROLES> = this.store.select<USER_ROLES>(UserState.userRole);
  private user$: Observable<IUser> = this.store.select<IUser>(UserState.user);
  public navItems: INavigationItem[] = homeMenuTiles;
  protected readonly USER_ROLES = USER_ROLES;
  public readonly reportingLink = environment.reporting;
  public readonly serviceDeskLink = environment.serviceDeskLink;

  public theme: 'dark' | 'light' = this.headerOperations.getCurrentTheme();
  public language: string = this.headerOperations.getCurrentLanguage();

  private destroy$: Subject<boolean> = new Subject<boolean>();

  /**
   * Maps the currently active sidebar tab to a mat-tab index. The Navigation tab
   * only exists on mobile, so on desktop the News/Notifications tabs shift down by one.
   */
  protected readonly selectedTabIndex = computed(() => {
    const tab = this.sidebarService.activeTab();
    if (this.sidebarService.isMobile()) {
      // Tab order: [Navigation = 0, News = 1, Notifications = 2]
      switch (tab) {
        case SidebarTab.Navigation:
          return 0;
        case SidebarTab.News:
          return 1;
        case SidebarTab.Notifications:
          return 2;
        default:
          return 0;
      }
    }
    // Desktop tab order (Navigation tab hidden): [News = 0, Notifications = 1]
    return tab === SidebarTab.Notifications ? 1 : 0;
  });

  ngOnInit(): void {
    this.loadNavigationItems();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  // Theme and Language operations
  public changeLang(langCode: string): void {
    this.headerOperations.changeLang(langCode);
    this.language = langCode;
  }

  public setTheme(mode: 'dark' | 'light'): void {
    this.headerOperations.setTheme(mode);
    this.theme = mode;
  }

  // Dialog operations
  public openApplicationFilterDialog(): void {
    this.headerOperations.openApplicationFilterDialog();
  }

  public openTimesheetSettingsDialog(): void {
    this.headerOperations.openTimesheetSettingsDialog();
  }

  protected openCreateNewsDialog(): void {
    this.headerOperations.openCreateNewsDialog();
  }

  protected openEditImpersonationDialog(): void {
    this.headerOperations.openEditImpersonationDialog();
  }

  protected openSelectImpersonationDialog(): void {
    this.headerOperations.openSelectImpersonationDialog();
  }

  protected stopImpersonation(): void {
    this.headerOperations.stopImpersonation();
  }

  // Navigation operations
  protected navigateToPersonalInformationPage(): void {
    this.headerOperations.navigateToPersonalInformationPage();
  }

  protected navigateToReleaseNotesPage(): void {
    this.headerOperations.navigateToReleaseNotesPage();
  }

  protected isImpersonating(): boolean {
    return this.headerOperations.isImpersonating();
  }

  protected closeSidebar(): void {
    this.headerOperations.closeSidebar();
  }

  private loadNavigationItems(): void {
    this.user$
      .pipe(
        filter((user: IUser) => !!user.role),
        tap((user: IUser) => (this.navItems = NavigationOperations.filterByUser(homeMenuTiles, user))),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }
}
